import {
  Address,
  BigNumber,
  FormattableCurrency,
  toNativeCurrencyString,
} from '@ttx/core';
import {useMemo, useState} from 'react';
import {useRouter} from 'next/router';
import {useAuthenticationContext} from '../contexts/authentication-context';
import {WalletNetwork} from '../contexts/authentication-context/types';
import {usePriceConversion} from './use-price-conversion';
import {usePaymentStatus} from './use-payment-status';
import {useQuantitySelector} from './use-quantity-selector';
import {CashValues, CryptoValues, ModalVariant} from '../utils/types';
import {useTreasuryBalance} from './use-treasury-balance';

const TRANSACTION_FEE_PERCENTAGE = BigNumber.from(10); // 10%

export const useCheckoutHelpers: (props: {
  network?: WalletNetwork;
  currencySymbol: FormattableCurrency | null;
  buyoutPrice: BigNumber;
  quantity: BigNumber;
  maxAllowedUnits: number;
}) => {
  monetaryValues: {
    crypto: CryptoValues;
    cash: CashValues;
  };
  paymentStatus?: string;
  modalVariant: ModalVariant | null;
  setModalVariant: (modalType: ModalVariant | null) => void;
  loadingTreasuryBalance?: boolean;
  purchaseQuantity: number;
  onIncrement: () => void;
  onDecrement: () => void;
  onResetQuantity: (qty: number) => void;
  canUseCash: boolean;
  walletAddress: Address | null;
  walletNetwork: WalletNetwork | null;
  userId: string | null;
  userEmail?: string;
} = ({network, currencySymbol, buyoutPrice, quantity, maxAllowedUnits}) => {
  const router = useRouter();
  const {walletNetwork, userId, walletAddress} = useAuthenticationContext();

  const {paymentStatus, userEmail} = usePaymentStatus();
  const [modalVariant, setModalVariant] = useState<ModalVariant | null>(null);

  const {convertCryptoToUSD} = usePriceConversion({
    primaryCurrency: currencySymbol,
    shouldPoll: modalVariant === 'cash-payment-details',
  });

  const {
    canUseCash,
    maxQuantityAvailableToPurchase,
    isLoading: loadingTreasuryBalance,
  } = useTreasuryBalance({
    amount: BigNumber.from(buyoutPrice || 0),
    currency: currencySymbol,
    network,
  });

  const {onIncrement, onDecrement, onResetQuantity, purchaseQuantity} =
    useQuantitySelector({
      availableQuantity: Number(quantity),
      maxQuantity: maxAllowedUnits,
      maxQuantityAvailableToPurchase,
      isCashModal: modalVariant === 'cash-payment-details',
      initialPurchaseQty: router.asPath.includes('revolution') ? 5 : 1, // walletAddress ? maxAllowedUnits : 1,
      isTraxxStemz: router.asPath.includes('revolution'),
      isEmilG: router.asPath.includes('emil'),
      isJoshSavage: router.asPath.includes('josh'),
    });

  const monetaryValues = useMemo(() => {
    if (!buyoutPrice || !Number(buyoutPrice) || !currencySymbol)
      return {
        crypto: {
          price: null,
          totalPerNFT: null,
          total: null,
          transactionFee: null,
          currencySymbol: null,
          originalPrice: null,
        },
        cash: {
          price: null,
          totalPerNFT: null,
          total: null,
          transactionFee: null,
          currencySymbol: null,
          originalPrice: null,
        },
      };

    const pricePerNFT = BigNumber.from(buyoutPrice);
    const totalCost = pricePerNFT.mul(purchaseQuantity);
    const transactionFeeCost = totalCost
      .mul(TRANSACTION_FEE_PERCENTAGE)
      .div(BigNumber.from(100));
    const nftCost = totalCost.sub(transactionFeeCost);

    return {
      crypto: {
        price: toNativeCurrencyString(nftCost, currencySymbol),
        totalPerNFT: toNativeCurrencyString(pricePerNFT, currencySymbol),
        total: toNativeCurrencyString(totalCost, currencySymbol),
        transactionFee: toNativeCurrencyString(
          transactionFeeCost,
          currencySymbol,
        ),
        currencySymbol,
        originalPrice: toNativeCurrencyString(pricePerNFT, currencySymbol),
      },
      cash: {
        price: convertCryptoToUSD(nftCost),
        totalPerNFT: convertCryptoToUSD(pricePerNFT),
        total: convertCryptoToUSD(totalCost),
        transactionFee: convertCryptoToUSD(transactionFeeCost),
        currencySymbol: 'USDT',
        originalPrice: convertCryptoToUSD(pricePerNFT),
      },
    };
  }, [buyoutPrice, convertCryptoToUSD, currencySymbol, purchaseQuantity]);

  return {
    monetaryValues,
    paymentStatus,
    modalVariant,
    setModalVariant,
    loadingTreasuryBalance,
    onIncrement,
    onDecrement,
    onResetQuantity,
    walletAddress,
    walletNetwork,
    userId,
    userEmail,
    canUseCash: canUseCash && !!monetaryValues.cash.price,
    purchaseQuantity,
  };
};
